import { useId, useState } from "react";
import imerSecWordmarkUrl from "../assets/imersec-wordmark-approved.svg";
import { R8FailureMark, R8Icon } from "./icons";
import type {
  R8HeaderControlId,
  R8HeaderControlPresentation,
  R8HeaderControls,
  R8TrustPresentation,
} from "./presentation";
import "./r8.css";

export interface PersistentOperationalHeaderProps {
  plantName: string;
  trust: R8TrustPresentation;
  controls: R8HeaderControls;
  onBrandSelect?: () => void;
  onControlSelect?: (control: R8HeaderControlPresentation) => void;
  wordmarkUrl?: string;
  semanticControls?: boolean;
  className?: string;
}

export function PersistentOperationalHeader({
  plantName,
  trust,
  controls,
  onBrandSelect,
  onControlSelect,
  wordmarkUrl = imerSecWordmarkUrl,
  semanticControls = false,
  className,
}: PersistentOperationalHeaderProps) {
  const [openControlId, setOpenControlId] = useState<R8HeaderControlId | null>(null);
  const popoverId = useId();
  const openControl = controls.find((control) => control.id === openControlId);
  const rootClassName = ["r8-operational-header", className].filter(Boolean).join(" ");

  function selectControl(control: R8HeaderControlPresentation) {
    setOpenControlId((current) => current === control.id ? null : control.id);
    onControlSelect?.(control);
  }

  const brandContent = (
    <>
      <img className="r8-brand__wordmark" src={wordmarkUrl} alt="imerSEC" />
      <strong className="r8-brand__plant">{plantName}</strong>
    </>
  );

  return (
    <header className={rootClassName} data-ui-contract="r8-iconic">
      <div className="r8-operational-header__identity">
        {onBrandSelect ? (
          <button className="r8-brand" type="button" onClick={onBrandSelect} aria-label={`Abrir visão operacional de ${plantName}`}>
            {brandContent}
          </button>
        ) : (
          <div className="r8-brand">{brandContent}</div>
        )}

        <div
          className="r8-trust"
          data-tone={trust.tone}
          role="status"
          aria-label={trust.accessibleLabel}
          title={trust.accessibleLabel}
        >
          <span className="r8-trust__led" aria-hidden="true" />
          <span className="r8-trust__age" aria-hidden="true">{trust.shortAge}</span>
          <span className="r8-sr-only">{trust.stateLabel}</span>
        </div>
      </div>

      <nav className="r8-header-controls" aria-label="Controles operacionais persistentes">
        {controls.map((control) => {
          const expanded = control.id === openControlId;
          const accessibleLabel = `${control.label}: ${control.stateLabel}. ${control.description}`;

          return (
            <button
              className="r8-header-control"
              type="button"
              key={control.id}
              data-control={semanticControls ? control.id : undefined}
              data-tone={control.tone}
              data-phase={control.phase}
              data-pressed={control.pressed}
              data-availability={control.availability}
              aria-label={accessibleLabel}
              aria-expanded={expanded}
              aria-controls={expanded ? popoverId : undefined}
              title={accessibleLabel}
              onClick={() => selectControl(control)}
            >
              <R8Icon className="r8-header-control__icon" icon={control.icon} />
              {control.phase === "failure" ? <R8FailureMark className="r8-header-control__failure" /> : null}
              <span className="r8-sr-only">{control.stateLabel}</span>
            </button>
          );
        })}
      </nav>

      {openControl ? (
        <div className="r8-header-popover" id={popoverId} role="status" aria-live="polite">
          <strong>{openControl.label}</strong>
          <span>{openControl.stateLabel}</span>
          <p>{openControl.description}</p>
        </div>
      ) : null}
    </header>
  );
}
